import React, { useState, useMemo } from 'react';
import { Search, Download, RefreshCw, Eye, CheckCircle, XCircle, School, Trash2, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useRegistrations, useSchools, useEvents, useRegistrationMutations } from '../../hooks/useConvex';
import { Registration, School as SchoolType, Event } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { Id } from '../../../convex/_generated/dataModel';

export const RegistrationsPage: React.FC = () => {
    const registrationsData = useRegistrations();
    const schoolsData = useSchools();
    const eventsData = useEvents();
    const { approve, reject, remove } = useRegistrationMutations();
    const loading = registrationsData === undefined || schoolsData === undefined || eventsData === undefined;

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<Registration['status'] | 'all'>('all');
    const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { user } = useAuth();
    const isChief = user?.email === 'chaitanyamishra.ai@gmail.com';

    // Transform Convex data
    const registrations = useMemo(() => {
        if (!registrationsData) return [];
        return registrationsData.map((r: Record<string, unknown>) => ({
            ...r,
            registration_id: r._id,
        })) as Registration[];
    }, [registrationsData]);

    const schools = useMemo(() => {
        if (!schoolsData) return {} as Record<string, SchoolType>;
        return schoolsData.reduce((acc: Record<string, SchoolType>, school: Record<string, unknown>) => {
            acc[school._id as string] = { ...school, school_id: school._id } as SchoolType;
            return acc;
        }, {} as Record<string, SchoolType>);
    }, [schoolsData]);

    const events = useMemo(() => {
        if (!eventsData) return {} as Record<string, Event>;
        return eventsData.reduce((acc: Record<string, Event>, event: Record<string, unknown>) => {
            acc[event._id as string] = { ...event, event_id: event._id } as Event;
            return acc;
        }, {} as Record<string, Event>);
    }, [eventsData]);

    const filteredRegistrations = useMemo(() => {
        let filtered = registrations;

        if (statusFilter !== 'all') {
            filtered = filtered.filter(reg => reg.status === statusFilter);
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(reg => {
                const schoolName = schools[reg.school_id]?.school_name.toLowerCase() || '';
                const teacherName = reg.teacher_incharge?.name?.toLowerCase() || '';
                return schoolName.includes(query) || teacherName.includes(query);
            });
        }

        return filtered;
    }, [searchQuery, statusFilter, registrations, schools]);

    const handleStatusUpdate = async (registrationId: string, newStatus: 'approved' | 'rejected', reason?: string) => {
        try {
            setActionLoading(true);
            if (newStatus === 'approved') {
                await approve({ registrationId: registrationId as Id<'registrations'> });
            } else {
                await reject({
                    registrationId: registrationId as Id<'registrations'>,
                    reason: reason || 'No reason provided'
                });
            }

            setIsDetailsModalOpen(false);
            setIsRejectModalOpen(false);
            setRejectionReason('');
            setSelectedRegistration(null);
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update registration status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteRegistration = async (registrationId: string, schoolName: string) => {
        if (!window.confirm(`Are you sure you want to DELETE the registration for "${schoolName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            setDeletingId(registrationId);
            await remove({ registrationId: registrationId as Id<'registrations'> });
            setIsDetailsModalOpen(false);
            setSelectedRegistration(null);
        } catch (error: any) {
            console.error('Error deleting registration:', error);
            alert(error.message || 'Failed to delete registration');
        } finally {
            setDeletingId(null);
        }
    };

    const getStatusBadge = (status: Registration['status']) => {
        const config: Record<string, 'success' | 'warning' | 'error' | 'neutral' | 'neon'> = {
            approved: 'success',
            submitted: 'neon',
            rejected: 'error',
            draft: 'neutral',
        };
        return config[status] || 'neutral';
    };

    const handleExportPDF = () => {
        const data = filteredRegistrations.map(reg => [
            (schools[reg.school_id]?.school_name || 'Unknown School') + (schools[reg.school_id]?.branch_name ? ` (${schools[reg.school_id]?.branch_name})` : ''),
            reg.status.toUpperCase(),
            reg.teacher_incharge?.name || 'N/A',
            reg.teacher_incharge?.phone || 'N/A',
            (reg.students?.length || 0).toString(),
            reg.submitted_at ? new Date(reg.submitted_at).toLocaleString() : 'Not Submitted'
        ]);

        pdfService.generatePDF({
            title: 'Registrations List',
            filename: 'registrations_list',
            columns: ['School', 'Status', 'Teacher', 'Contact', 'Students', 'Submitted At'],
            data: data,
            orientation: 'landscape',
        });
    };

    if (loading) return <Loader fullScreen />;

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <ClipboardList className="w-5 h-5 text-brand" />
                        </div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Registrations</h1>
                            <Badge variant="neutral" size="sm">{registrations.length}</Badge>
                        </div>
                    </div>
                    <p className="text-sm text-white/30 mt-0.5 ml-[52px]">Manage school registrations and approvals</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.reload()}
                        isLoading={loading}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportPDF}>
                        <Download className="w-4 h-4 mr-2" />
                        Export PDF
                    </Button>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-3 flex items-center gap-3">
                <div className="flex-1">
                    <Input
                        placeholder="Search by school name or teacher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        leftIcon={<Search className="w-4 h-4 text-white/30" />}
                        fullWidth
                    />
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-4 py-2 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-200 ${
                            statusFilter === 'all'
                                ? 'bg-brand/[0.08] text-brand border border-brand/20'
                                : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:border-white/[0.1]'
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setStatusFilter('submitted')}
                        className={`px-4 py-2 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-200 ${
                            statusFilter === 'submitted'
                                ? 'bg-brand/[0.08] text-brand border border-brand/20'
                                : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:border-white/[0.1]'
                        }`}
                    >
                        Submitted
                    </button>
                    <button
                        onClick={() => setStatusFilter('approved')}
                        className={`px-4 py-2 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-200 ${
                            statusFilter === 'approved'
                                ? 'bg-brand/[0.08] text-brand border border-brand/20'
                                : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:border-white/[0.1]'
                        }`}
                    >
                        Approved
                    </button>
                    <button
                        onClick={() => setStatusFilter('rejected')}
                        className={`px-4 py-2 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-200 ${
                            statusFilter === 'rejected'
                                ? 'bg-brand/[0.08] text-brand border border-brand/20'
                                : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:border-white/[0.1]'
                        }`}
                    >
                        Rejected
                    </button>
                </div>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-white/[0.04] flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white/70">All Registrations</h3>
                    <Badge variant="neutral" size="sm">{filteredRegistrations.length} results</Badge>
                </div>
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-white/[0.04]">
                            <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">School</th>
                            <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Status</th>
                            <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Teacher</th>
                            <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Students</th>
                            <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Submitted</th>
                            <th className="px-6 py-3.5 text-right text-[11px] font-medium uppercase tracking-wider text-white/25">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.03]">
                        {filteredRegistrations.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-16 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-white/[0.03] flex items-center justify-center">
                                            <ClipboardList className="w-6 h-6 text-white/20" />
                                        </div>
                                        <p className="text-sm text-white/30">No registrations found</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredRegistrations.map((reg) => (
                                <tr key={reg.registration_id} className="hover:bg-white/[0.02] transition-colors duration-150">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center shrink-0">
                                                <School className="w-4 h-4 text-brand" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-white/90 truncate">
                                                    {schools[reg.school_id]?.school_name || 'Unknown School'}
                                                </p>
                                                {schools[reg.school_id]?.email && (
                                                    <p className="text-xs text-white/30 font-mono truncate mt-0.5">
                                                        {schools[reg.school_id]?.email}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant={getStatusBadge(reg.status)} size="sm">
                                            {reg.status.toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm text-white/70">{reg.teacher_incharge?.name || 'N/A'}</span>
                                            {reg.teacher_incharge?.phone && (
                                                <span className="text-xs text-white/30 font-mono mt-0.5">{reg.teacher_incharge.phone}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-2xl font-mono font-bold text-white/90">{reg.students?.length || 0}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-xs text-white/40 font-mono">
                                            {reg.submitted_at ? new Date(reg.submitted_at).toLocaleDateString() : '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedRegistration(reg);
                                                    setIsDetailsModalOpen(true);
                                                }}
                                                className="w-8 h-8 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-white/[0.1] flex items-center justify-center transition-all duration-200 group"
                                            >
                                                <Eye className="w-4 h-4 text-white/40 group-hover:text-brand transition-colors" />
                                            </button>
                                            {reg.status === 'submitted' && (
                                                <>
                                                    <button
                                                        onClick={() => handleStatusUpdate(reg.registration_id, 'approved')}
                                                        disabled={actionLoading}
                                                        className="w-8 h-8 rounded-lg bg-white/[0.03] hover:bg-[#10B981]/[0.08] border border-white/[0.06] hover:border-[#10B981]/20 flex items-center justify-center transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <CheckCircle className="w-4 h-4 text-white/40 group-hover:text-[#10B981] transition-colors" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedRegistration(reg);
                                                            setIsRejectModalOpen(true);
                                                        }}
                                                        disabled={actionLoading}
                                                        className="w-8 h-8 rounded-lg bg-white/[0.03] hover:bg-[#EF4444]/[0.08] border border-white/[0.06] hover:border-[#EF4444]/20 flex items-center justify-center transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <XCircle className="w-4 h-4 text-white/40 group-hover:text-[#EF4444] transition-colors" />
                                                    </button>
                                                </>
                                            )}
                                            {isChief && (
                                                <button
                                                    onClick={() => handleDeleteRegistration(reg.registration_id, schools[reg.school_id]?.school_name || 'Unknown')}
                                                    disabled={actionLoading || deletingId === reg.registration_id}
                                                    className="w-8 h-8 rounded-lg bg-white/[0.03] hover:bg-[#EF4444]/[0.08] border border-white/[0.06] hover:border-[#EF4444]/20 flex items-center justify-center transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Trash2 className="w-4 h-4 text-white/40 group-hover:text-[#EF4444] transition-colors" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {filteredRegistrations.length === 0 ? (
                    <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-white/[0.03] flex items-center justify-center">
                                <ClipboardList className="w-6 h-6 text-white/20" />
                            </div>
                            <p className="text-sm text-white/30">No registrations found</p>
                        </div>
                    </div>
                ) : (
                    filteredRegistrations.map((reg) => (
                        <div key={reg.registration_id} className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4 hover:border-white/[0.1] transition-all duration-200">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center shrink-0">
                                        <School className="w-4 h-4 text-brand" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-white/90 truncate">
                                            {schools[reg.school_id]?.school_name || 'Unknown School'}
                                        </p>
                                        {schools[reg.school_id]?.email && (
                                            <p className="text-xs text-white/30 font-mono truncate mt-0.5">
                                                {schools[reg.school_id]?.email}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Badge variant={getStatusBadge(reg.status)} size="sm">
                                    {reg.status.toUpperCase()}
                                </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Teacher</p>
                                    <p className="text-sm text-white/70">{reg.teacher_incharge?.name || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Students</p>
                                    <p className="text-xl font-mono font-bold text-white/90">{reg.students?.length || 0}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]">
                                <p className="text-xs text-white/30 font-mono">
                                    {reg.submitted_at ? new Date(reg.submitted_at).toLocaleDateString() : 'Not submitted'}
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedRegistration(reg);
                                        setIsDetailsModalOpen(true);
                                    }}
                                >
                                    <Eye className="w-4 h-4 mr-2" />
                                    View
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Registration Details Modal */}
            <Modal
                isOpen={isDetailsModalOpen}
                onClose={() => {
                    setIsDetailsModalOpen(false);
                    setSelectedRegistration(null);
                }}
                title="Registration Details"
                size="lg"
            >
                {selectedRegistration && (
                    <div className="space-y-6">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                            <div>
                                <h3 className="text-lg font-display font-bold text-white">
                                    {schools[selectedRegistration.school_id]?.school_name}
                                </h3>
                                <p className="text-xs text-white/30 mt-1">
                                    Submitted: {selectedRegistration.submitted_at ? new Date(selectedRegistration.submitted_at).toLocaleString() : 'Not submitted'}
                                </p>
                            </div>
                            <Badge variant={getStatusBadge(selectedRegistration.status)} size="md">
                                {selectedRegistration.status.toUpperCase()}
                            </Badge>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                                <h4 className="text-[10px] font-semibold text-white/30 mb-3 uppercase tracking-wider">Teacher In-charge</h4>
                                <div className="space-y-2 text-sm">
                                    <p className="text-white/70">
                                        <span className="text-white/40">Name:</span> {selectedRegistration.teacher_incharge?.name || 'N/A'}
                                    </p>
                                    <p className="text-white/70">
                                        <span className="text-white/40">Phone:</span> {selectedRegistration.teacher_incharge?.phone || 'N/A'}
                                    </p>
                                    <p className="text-white/70">
                                        <span className="text-white/40">Email:</span> {selectedRegistration.teacher_incharge?.email || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                                <h4 className="text-[10px] font-semibold text-white/30 mb-3 uppercase tracking-wider">Statistics</h4>
                                <div className="space-y-2 text-sm">
                                    <p className="text-white/70">
                                        <span className="text-white/40">Total Students:</span> {selectedRegistration.students?.length || 0}
                                    </p>
                                    <p className="text-white/70">
                                        <span className="text-white/40">Events:</span> {new Set(selectedRegistration.students?.flatMap(s => s.events) || []).size}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Students List */}
                        <div>
                            <h4 className="text-[10px] font-semibold text-white/30 mb-3 uppercase tracking-wider">Registered Students</h4>
                            <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-white/[0.04]">
                                            <th className="px-4 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/25">Name</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/25">Grade</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/25">Events</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.03]">
                                        {selectedRegistration.students?.map((student, idx) => (
                                            <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                                                <td className="px-4 py-3 text-white/70">{student.name}</td>
                                                <td className="px-4 py-3 text-white/40">{student.grade}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {student.events.map(eventId => (
                                                            <span key={eventId} className="px-2 py-0.5 rounded-full bg-brand/[0.08] border border-brand/[0.12] text-brand text-[10px]">
                                                                {events[eventId]?.name || eventId}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Chief Delete Button */}
                        {isChief && (
                            <div className="flex justify-start pt-4 border-t border-white/[0.06]">
                                <Button
                                    variant="ghost"
                                    onClick={() => handleDeleteRegistration(
                                        selectedRegistration.registration_id,
                                        schools[selectedRegistration.school_id]?.school_name || 'Unknown'
                                    )}
                                    disabled={actionLoading || !!deletingId}
                                    isLoading={deletingId === selectedRegistration.registration_id}
                                    className="text-red-500 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Registration
                                </Button>
                            </div>
                        )}

                        {/* Actions */}
                        {selectedRegistration.status === 'submitted' && (
                            <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.06]">
                                <Button
                                    variant="danger"
                                    onClick={() => setIsRejectModalOpen(true)}
                                    disabled={actionLoading}
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => handleStatusUpdate(selectedRegistration.registration_id, 'approved')}
                                    isLoading={actionLoading}
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Approve
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal
                isOpen={isRejectModalOpen}
                onClose={() => setIsRejectModalOpen(false)}
                title="Reject Registration"
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-sm text-white/60">
                        Please provide a reason for rejecting this registration. This will be sent to the school.
                    </p>
                    <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white placeholder:text-white/20 min-h-[120px] focus:border-brand focus:outline-none transition-colors resize-none"
                        placeholder="Enter rejection reason..."
                    />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="ghost" onClick={() => setIsRejectModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => selectedRegistration && handleStatusUpdate(selectedRegistration.registration_id, 'rejected', rejectionReason)}
                            isLoading={actionLoading}
                            disabled={!rejectionReason.trim()}
                        >
                            Confirm Rejection
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
